import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/api";
import ImageUploadField from "@/pages/admin/ImageUploadField";

const GRADIENT_PRESETS = [
  { label: "Sage → Forest", value: "linear-gradient(135deg, #C2CCAF 0%, #5B7544 100%)" },
  { label: "Sand → Bronze", value: "linear-gradient(135deg, #E5D7B7 0%, #B8975A 100%)" },
  { label: "Olive → Deep Green", value: "linear-gradient(135deg, #8FA678 0%, #3F5A2E 100%)" },
  { label: "Wheat → Umber", value: "linear-gradient(135deg, #D4B872 0%, #8C7438 100%)" },
  { label: "Cream → Terracotta", value: "linear-gradient(135deg, #F0DFC9 0%, #A85D3A 100%)" },
  { label: "Slate → Charcoal", value: "linear-gradient(135deg, #B8BFB1 0%, #4A4E45 100%)" },
];

const BLANK_LEADER = {
  name: "", designation: "", initials: "", bg: GRADIENT_PRESETS[2].value,
  blurb: "", linkedin_url: "", image_url: "", display_order: 0, is_active: true,
};

export default function TeamMembersView() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/leaders");
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete team member "${name}"?`)) return;
    await api.delete(`/admin/leaders/${id}`);
    load();
  };

  return (
    <div className="p-5 sm:p-8 md:p-12" data-testid="admin-team">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[var(--k-text)]">Team Members</h1>
          <p className="text-[var(--k-text-2)] mt-2">
            {items.length} people shown on the Brands page.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...BLANK_LEADER, display_order: items.length + 1 })}
          className="btn-green"
          data-testid="admin-add-leader-btn"
        >
          <Plus className="w-4 h-4" /> Add Team Member
        </button>
      </div>

      {loading ? (
        <div className="font-mono-label text-[var(--k-muted)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="organic-card p-10 text-center">
          <p className="text-[var(--k-text-2)]">No team members yet. Click "Add Team Member" to create one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((l) => (
            <div key={l.id} className="organic-card p-6" data-testid={`admin-leader-card-${l.id}`}>
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center shrink-0 border-2 border-[var(--k-card)] shadow"
                  style={{ background: l.bg }}
                >
                  {l.image_url ? (
                    <img src={l.image_url} alt={l.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-serif-display text-lg text-[var(--k-card)]">{l.initials}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif-display text-lg text-[var(--k-text)] truncate">{l.name}</div>
                  <div className="font-mono-label text-[var(--k-green)] text-[0.6rem] truncate">{l.designation}</div>
                  <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem] mt-1">
                    Order {l.display_order} · {l.is_active ? "Visible" : "Hidden"}
                  </div>
                </div>
              </div>
              <p className="text-[var(--k-text-2)] text-xs leading-relaxed line-clamp-2 mb-4">{l.blurb}</p>
              <div className="flex gap-3 text-[0.6rem] font-mono-label">
                <button
                  onClick={() => setEditing(l)}
                  className="text-[var(--k-green)]"
                  data-testid={`admin-edit-leader-${l.id}`}
                >
                  <Edit className="w-3 h-3 inline mr-1" /> Edit
                </button>
                <button
                  onClick={() => remove(l.id, l.name)}
                  className="text-red-500"
                  data-testid={`admin-delete-leader-${l.id}`}
                >
                  <Trash2 className="w-3 h-3 inline mr-1" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <LeaderEditor
          leader={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function LeaderEditor({ leader, onClose, onSaved }) {
  const isNew = !leader.id;
  const [form, setForm] = useState({ ...leader });
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const set = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: k === "display_order" ? Number(e.target.value) || 0 : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    const payload = {
      name: form.name.trim(),
      designation: form.designation.trim(),
      initials: (form.initials || "").trim(),
      bg: form.bg || "",
      blurb: form.blurb || "",
      linkedin_url: form.linkedin_url || "",
      image_url: form.image_url || "",
      display_order: Number(form.display_order) || 0,
      is_active: form.is_active !== false,
    };
    try {
      if (isNew) await api.post("/admin/leaders", payload);
      else await api.put(`/admin/leaders/${leader.id}`, payload);
      onSaved();
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err?.response?.data?.detail) });
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="admin-leader-editor">
      <div className="bg-[var(--k-card)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-[var(--k-line)]">
          <h2 className="font-serif-display text-2xl text-[var(--k-text)]">
            {isNew ? "Add Team Member" : "Edit Team Member"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border border-[var(--k-line)] flex items-center justify-center text-[var(--k-text-2)]"
            aria-label="Close"
            data-testid="admin-leader-editor-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-x-7 gap-y-5">
            <Field label="Name *">
              <input className="kostra-input" required value={form.name} onChange={set("name")} data-testid="leader-input-name" />
            </Field>
            <Field label="Designation *">
              <input className="kostra-input" required value={form.designation} onChange={set("designation")} data-testid="leader-input-designation" placeholder="Co-Founder / CFO / VP Marketing" />
            </Field>
            <Field label="Initials (auto if blank)">
              <input className="kostra-input" value={form.initials} onChange={set("initials")} maxLength={3} placeholder="MS" data-testid="leader-input-initials" />
            </Field>
            <Field label="Display order">
              <input type="number" className="kostra-input" value={form.display_order} onChange={set("display_order")} data-testid="leader-input-order" />
            </Field>
            <Field label="LinkedIn URL" full>
              <input className="kostra-input" value={form.linkedin_url} onChange={set("linkedin_url")} placeholder="https://linkedin.com/in/…" data-testid="leader-input-linkedin" />
            </Field>
            <Field label="Bio / one-line blurb" full>
              <textarea className="kostra-input resize-none" rows={2} value={form.blurb} onChange={set("blurb")} data-testid="leader-input-blurb" />
            </Field>
            <Field label="Avatar gradient (used when no photo is attached)" full>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="leader-bg-presets">
                {GRADIENT_PRESETS.map((g) => {
                  const selected = form.bg === g.value;
                  return (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, bg: g.value }))}
                      className={`w-10 h-10 rounded-full border-2 transition-transform ${
                        selected ? "border-[var(--k-green)] scale-110" : "border-transparent"
                      }`}
                      style={{ background: g.value }}
                      title={g.label}
                      aria-label={g.label}
                      data-testid={`leader-bg-preset-${g.label.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                  );
                })}
              </div>
              <input
                className="kostra-input"
                value={form.bg}
                onChange={set("bg")}
                placeholder="linear-gradient(135deg, #… 0%, #… 100%)"
                data-testid="leader-input-bg"
              />
            </Field>
            <ImageUploadField
              label="Profile Photo (optional — overrides initials)"
              value={form.image_url}
              onChange={(url) => setForm((p) => ({ ...p, image_url: url }))}
              testId="leader-image"
            />
            <Field full>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active !== false}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                  data-testid="leader-input-active"
                />
                <span className="font-mono-label text-[var(--k-muted)]">Show on Brands page</span>
              </label>
            </Field>
          </div>

          {status.state === "error" && (
            <div className="border border-red-300 bg-red-50 p-3 rounded text-xs text-red-700" data-testid="leader-editor-error">
              {status.msg}
            </div>
          )}

          <button
            type="submit"
            disabled={status.state === "loading"}
            className="btn-green w-full"
            data-testid="leader-save-btn"
          >
            {status.state === "loading" ? "Saving…" : isNew ? "Create Team Member" : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <label className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}>
      {label && <span className="font-mono-label text-[var(--k-muted)]">{label}</span>}
      {children}
    </label>
  );
}
